import { jwtDecode } from 'jwt-decode';
import { getEasternDateParts } from './dateUtils.ts';
import { version } from '../Contexts/variables.jsx';

type UserToken = {
    scout: string;
    team: string;
    exp?: number;
    threshold?: number;
    [key: string]: any;
};

const AUTH_STORAGE_KEY = 'user2026';
const USAGE_ENDPOINT = 'https://www.fbxchange.com/ReactPHP/usage.php';

export const clearAuthUser = (): void => {
    try {
        localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch (error) {
        console.error('Error clearing auth token:', error);
    }
};

export const saveAuthUser = (token: string): void => {
    localStorage.setItem(AUTH_STORAGE_KEY, token);
};

export const getStoredUserToken = (): string | null => {
    return localStorage.getItem(AUTH_STORAGE_KEY);
};

export const isPermissionDenied = (value: unknown): boolean => {
    if (value === undefined) return false;

    const normalizedValue = String(value ?? '').trim().toLowerCase();
    return !['yes', 'true', '1', 'on', 'y'].includes(normalizedValue);
};

export const buildAuthedFormBody = (values: Record<string, string>): URLSearchParams => {
    const body = new URLSearchParams(values);
    const token = getStoredUserToken();
    if (token) {
        body.set('token', token);
    }
    body.set('appVersion', version);
    return body;
};

export const buildAuthedJsonBody = (values: Record<string, any>): string => {
    const token = getStoredUserToken();
    return JSON.stringify({
        ...values,
        ...(token ? { token } : {}),
        appVersion: version,
    });
};

const SECURE_PHP_ENDPOINTS = [
    'transactions.php',
    'danTransactions.php',
    'notes.php',
    'scoutreports.php',
    'fileSection.php',
    'quickNotes.php',
    'draftBoard.php',
    'hdbox.php',
    'overview.php',
];

let secureFetchInstalled = false;

const isSecurePhpRequest = (input: RequestInfo | URL): boolean => {
    const url = typeof input === 'string'
        ? input
        : input instanceof URL
            ? input.toString()
            : input.url;

    return url.includes('/ReactPHP/')
        && SECURE_PHP_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

const addAuthToBody = (body: BodyInit | null | undefined): BodyInit | null | undefined => {
    const token = getStoredUserToken();
    if (!token) return body;

    if (body instanceof URLSearchParams) {
        body.set('token', token);
        body.set('appVersion', version);
        return body;
    }

    if (body instanceof FormData) {
        body.set('token', token);
        body.set('appVersion', version);
        return body;
    }

    if (typeof body === 'string') {
        const trimmed = body.trim();
        if (trimmed.startsWith('{')) {
            try {
                const parsed = JSON.parse(trimmed);
                return JSON.stringify({
                    ...parsed,
                    token,
                    appVersion: version,
                });
            } catch {
                return body;
            }
        }

        const params = new URLSearchParams(body);
        params.set('token', token);
        params.set('appVersion', version);
        return params.toString();
    }

    return body;
};

export const installSecureFetch = (): void => {
    if (secureFetchInstalled || typeof window === 'undefined') return;

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        if (!isSecurePhpRequest(input)) {
            return originalFetch(input, init);
        }

        const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));

        const nextInit: RequestInit = {
            ...init,
            headers,
            body: addAuthToBody(init?.body),
        };

        const response = await originalFetch(input, nextInit);

        if (response.status === 426) {
            response.clone().json()
                .then((data) => {
                    if (data?.forceRefresh) {
                        window.alert(data?.message || 'A new version is available. Refreshing now.');
                        window.location.reload();
                    }
                })
                .catch(() => {});
        }

        return response;
    };

    secureFetchInstalled = true;
};

export async function getUserFromToken(): Promise<UserToken | null> {
    const token = getStoredUserToken();

    if (!token) {
        forceLogoutToLogin();
        return null;
    }

    try {
        const decoded = jwtDecode<UserToken>(token);
        const now = Math.floor(Date.now() / 1000);

        if (
            decoded.exp !== undefined &&
            !Number.isNaN(Number(decoded.exp)) &&
            Number(decoded.exp) < now
        ) {
            forceLogoutToLogin();
            return null;
        }

        return decoded;
    } catch (error) {
        console.error('Error decoding token:', error);
        forceLogoutToLogin();
        return null;
    }
}

export async function getTokenFromLocalStorage(): Promise<UserToken | null> {
    return getUserFromToken();
}

export const trackUsageEvent = (
    eventLabel: string,
    options: {
        eventType?: string;
        source?: string;
        metadata?: Record<string, unknown>;
    } = {}
): void => {
    const cleanLabel = String(eventLabel || '').trim();
    if (!cleanLabel) return;

    let user: UserToken | null = null;
    const token = getStoredUserToken();
    if (!token) return;

    try {
        user = jwtDecode<UserToken>(token);
    } catch {
        return;
    }

    const body = new URLSearchParams({
        eventType: options.eventType || 'button_pressed',
        eventLabel: cleanLabel,
        userId: String(user?.scout ?? ''),
        userName: String(user?.displayName ?? ''),
        teamId: String(user?.team ?? ''),
        teamName: String(user?.team ?? ''),
	        source: options.source || 'frontend',
	        appVersion: version,
	        metadata: JSON.stringify(options.metadata || {}),
    });

    fetch(USAGE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
        keepalive: true,
    }).catch(() => {});
};

export function calculateAge(dob: string): string {
    if (dob && dob !== '0000-00-00') {
        const birthDate = new Date(dob);
        const today = getEasternDateParts();
        const todayYear = Number(today.year);
        const todayMonth = Number(today.month) - 1;
        const todayDay = Number(today.day);
        let age = todayYear - birthDate.getFullYear();
        const monthDifference = todayMonth - birthDate.getMonth();

        if (monthDifference < 0 || (monthDifference === 0 && todayDay < birthDate.getDate())) {
            age--;
        }

        return age.toString();
    } else {
        return '-';
    }
}

const logPlayerArray = (source: string, playerArray: any): void => {
    const firstRow = Array.isArray(playerArray) ? playerArray[0] : null;
    const cflidField = firstRow && typeof firstRow === 'object'
        ? ['cflid', 'cflId', 'CFLID'].find((key) => Object.prototype.hasOwnProperty.call(firstRow, key))
        : null;

    if (cflidField) {
        console.log(`[${source}] cflid field present: YES (${cflidField})`);
    } else {
        console.log(`[${source}] cflid field present: NO`);
    }
};



export const fetchDataQuery = async (list: string, user: string, team: string): Promise<any> => {
    console.log('Fetching data for list:', list, 'user:', user, 'team:', team);

    try {
        const response = await fetch('https://www.fbxchange.com/ReactPHP/reactSearch.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                method: 'getQueryData',
                savedQueryID: list,
                userID: user,
                team: team
            }).toString()
        });
        const data = await response.json();
        logPlayerArray('Saved Query', data);
        return data;
    } catch (error) { }
}

export const fetchData = async (list: string, user: string, team: string): Promise<any> => {
    try {
        const response = await fetch('https://www.fbxchange.com/ReactPHP/reactSearch.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                method: 'getLastSearchReact',
                list: list,
                user: user,
                team: team
            }).toString()
        });
        const data = await response.json();
        logPlayerArray('Last List', data);
        return data;
    } catch (error) { }
};

export const isTokenExpired = (token: string): boolean => {
    try {
        const { exp } = jwtDecode<{ exp: number }>(token);
        return exp < Date.now() / 1000;
    } catch (error) {
        return true;
    }
};

export const getMyList = async (user: string, team: string): Promise<string | null> => {
    try {
        const response = await fetch('https://www.fbxchange.com/ReactPHP/listBox.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: buildAuthedFormBody({
                method: 'MyLists',
                scout: user,
                team: team
            }).toString()
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to fetch token:', error);
        return null;
    }
};

export const getMyScouts = async (user: string, team: string): Promise<string | null> => {
    try {
        const response = await fetch('https://www.fbxchange.com/ReactPHP/reactSearch.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                method: 'getMyScouts',
                scout: user,
                team: team
            }).toString()
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to fetch token:', error);
        return null;
    }
};

export const getPlayersOnList = async (listID: string): Promise<any> => {
    try {
        const response = await fetch('https://www.fbxchange.com/ReactPHP/reactSearch.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                method: 'getListData',
                listID: listID
            }).toString()
        });

        const data = await response.json();
        return data;

    } catch (error) {
        console.error('Failed to fetch token:', error);
        return null;
    }
}

type PlayerData = {
    impnip: string;
    [key: string]: any;
};

export const getNipList = async (playerData: PlayerData[]): Promise<string | undefined> => {
    if (playerData) {
        const { nipCount, usaCount, gloCount, v4Count } = playerData.reduce(
            (counts, player) => {
                if (player.impnip === 'N') counts.nipCount += 1;
                else if (player.impnip === 'G') counts.gloCount += 1;
                else if (player.impnip.startsWith('V')) counts.v4Count += 1;
                else counts.usaCount += 1;
                return counts;
            },
            { nipCount: 0, usaCount: 0, gloCount: 0, v4Count: 0 }
        );
        return `${nipCount},${usaCount},${gloCount},${v4Count}`;
    }
};

export const saveToDB = (FBXID: string, listID: string): void => {
    try {
        fetch('https://www.fbxchange.com/ReactPHP/listBox.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: buildAuthedFormBody({
                method: 'updateList',
                listID: listID,
                FBXDATA: FBXID,
            }).toString(),
        }).catch(error => {
            console.error('Failed to update database:', error);
        });
    } catch (error) {
        console.error('Error in saveToDB function:', error);
    }
};

export const saveSharedToDB = (listID: string, sharedID: string): void => {
    fetch('https://www.fbxchange.com/ReactPHP/listBox.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: buildAuthedFormBody({
            method: 'updateSharedList',
            listID: listID,
            sharedToId: sharedID,
        }).toString(),
    }).catch(error => {
        console.error('Failed to update database:', error);
    });
};

export const runS1Search = async (searchValue: unknown): Promise<any> => {
    const user = await getUserFromToken();
    if (!user) return;
    try {
        const response = await fetch('https://www.fbxchange.com/ReactPHP/S1Search.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: buildAuthedFormBody({
                method: 'runSearch',
                scout: user.scout,
                team: user.team,
                appVersion: version,
                data: JSON.stringify(searchValue)
            }).toString()
        });
        const data = await response.json();
        if (parseInt(data.count) < 501) {
            logPlayerArray('S1 Search', data.returnArray);
            return data.returnArray;
        } else {
            alert('Your Search Resulted in ' + data.count + ' players, and we will only allow 500 Records to be displayed');
            const toManyArray = [{ playerTotal: data.count }];
            logPlayerArray('S1 Search Too Many', toManyArray);
            return toManyArray;
        }
    } catch (error) {
        console.error('Failed to fetch token:', error);
        return null;
    }
};

export const getPlayersFromList = async (listID: string, team: string, scout: string): Promise<any> => {
    try {
        const response = await fetch('https://www.fbxchange.com/ReactPHP/listBox.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: buildAuthedFormBody({
                method: 'ReturnList',
                listID: listID,
                team: team,
                scout: scout,
            }).toString()
        });
        const data = await response.json();
        logPlayerArray('Return List', data);
        return data;

    } catch (error) {
        console.error('Failed to fetch token:', error);
        return null;
    }
}

export const getAllSchools = async (): Promise<any> => {
    try {
        const response = await fetch('https://www.fbxchange.com/ReactPHP/reactSearch.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                method: 'getSchools',
            }).toString()
        });

        const data = await response.json();
        return data;

    } catch (error) {
        console.error('Failed to fetch token:', error);
        return null;
    }
}

export const runS2Event = async (searchValue: unknown): Promise<any> => {
    const user = await getUserFromToken();
    if (!user) return;
    try {
        const response = await fetch('https://www.fbxchange.com/ReactPHP/S2Search.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: buildAuthedFormBody({
                method: 'EventData',
                scout: user.scout,
                team: user.team,
                data: JSON.stringify(searchValue)
            }).toString()
        });
        const data = await response.json();
        logPlayerArray('S2 Event', data?.returnArray);
        if (parseInt(data.count) < 1000) {
            return data;
        } else {
            alert('Your Search Resulted in ' + data.count + ' players, and we will only allow 1000 Records to be displayed');
            return data;
        }
    } catch (error) {
        console.error('Failed to fetch token:', error);
        return null;
    }
};

export const runS2Scout = async (searchValue: Record<string, any>): Promise<any> => {
    const user = await getUserFromToken();
    if (!user) return;
    try {
        let whatSearch = 'ScoutSearch';
        if (!searchValue.snpicker) { whatSearch = 'NoteSearch'; }
        const response = await fetch('https://www.fbxchange.com/ReactPHP/S2Search.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: buildAuthedFormBody({
                method: whatSearch,
                scout: user.scout,
                team: user.team,
                data: JSON.stringify(searchValue)
            }).toString()
        });
        const data = await response.json();
        logPlayerArray(whatSearch === 'ScoutSearch' ? 'S2 Scout' : 'S2 Note', data?.returnArray);
        if (parseInt(data.count) < 1000) {
            return data;
        } else {
            alert('Your Search Resulted in ' + data.count + ' players, and we will only allow 1000 Records to be displayed');
            return data;
        }
    } catch (error) {
        console.error('Failed to fetch token:', error);
        return null;
    }
};

export const runS2Trans = async (searchValue: Record<string, any>): Promise<any> => {
    const user = await getUserFromToken();
    if (!user) return;
    try {
        const response = await fetch('https://www.fbxchange.com/ReactPHP/S2Search.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: buildAuthedFormBody({
                script: 'TransData',
                scout: String(user?.scout ?? ''),
                team: String(user?.team ?? ''),
                data: JSON.stringify(searchValue ?? {})
            })
        });
        const data = await response.json();
        logPlayerArray('S2 Transactions', data?.returnArray);

        if (data.count !== null && data.count !== undefined && parseInt(data.count) >= 1000) {
            await new Promise((resolve) => {
                alert(`Your Search Resulted in ${data.count} players, and we will only allow 1000 Records to be displayed`);
                resolve(null);
            });
        }

        return data;

    } catch (error) {
        console.error('Failed to fetch data:', error);
        return null;
    }
};

export const runS2Note = async (searchValue: Record<string, any>): Promise<any> => {
    const user = await getUserFromToken();
    if (!user) return;
    try {
        let whatSearch = 'NoteSearch';
        const response = await fetch('https://www.fbxchange.com/ReactPHP/S2Search.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: buildAuthedFormBody({
                method: whatSearch,
                scout: user.scout,
                team: user.team,
                data: JSON.stringify(searchValue)
            }).toString()
        });
        const data = await response.json();
        logPlayerArray('S2 Note', data?.returnArray);
        if (parseInt(data.count) < 1000) {
            return data;
        } else {
            alert('Your Search Resulted in ' + data.count + ' players, and we will only allow 1000 Records to be displayed');
            return data;
        }
    } catch (error) {
        console.error('Failed to fetch token:', error);
        return null;
    }
};

export const sendPlayerReportPHP = async (searchValue: Record<string, any>): Promise<void | null> => {
    const user = await getUserFromToken();
    if (!user) return;

    try {
        let whatSearch = 'sendPlayerErrorReport';

        const response = await fetch('https://www.fbxchange.com/ReactPHP/bugReport.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                script: whatSearch,
                scout: user.scout,
                team: user.team,
                user: user.scout,
                data: JSON.stringify(searchValue)
            }).toString()
        });
        const data = await response.json();
        console.log(data);
        alert('Your Report has been sent - Thank you');
    } catch (error) {
        console.error('Failed to fetch token:', error);
        return null;
    }
};

export const sendBugReportPHP = async (searchValue: Record<string, any>): Promise<void | null> => {
    const user = await getUserFromToken();
    if (!user) return;

    try {
        let whatSearch = 'sendBugReport';

        const response = await fetch('https://www.fbxchange.com/ReactPHP/bugReport.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                script: whatSearch,
                scout: user.scout,
                team: user.team,
                user: user.scout,
                data: JSON.stringify(searchValue)
            }).toString()
        });
        const data = await response.json();
        console.log(data);
        alert('Your Report has been sent - Thank you');
    } catch (error) {
        console.error('Failed to fetch token:', error);
        return null;
    }
};

export const updateSharedData = async (newString: string): Promise<void> => {
    const user = await getUserFromToken();
    if (!user) return;
    try {
        fetch('https://www.fbxchange.com/ReactPHP/loadData.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                method: 'setNewShare',
                team: user.team,
                user: user.scout,
                data: JSON.stringify(newString)
            }).toString()
        }).catch(error => console.error('Request failed:', error));
    } catch (error) {
        console.error('Unexpected error:', error);
    }
};

export const updateTheLog = async (addORremove: string, idToChange: string, byWho: string, listID: string): Promise<void> => {
    const user = await getUserFromToken();
    if (!user) return;

    try {
        fetch('https://www.fbxchange.com/ReactPHP/listBox.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: buildAuthedFormBody({
                method: 'updateLog',
                addOrRemove: addORremove,
                myDisplayName: user.displayname || 'Unknown',
                byWho: byWho,
                listID: listID,
                idChanged: idToChange
            }).toString()
        }).catch(error => console.error('Request failed:', error));
    } catch (error) {
        console.error('Unexpected error:', error);
    }
}

export const decodeFBX = () => {
    const token = localStorage.getItem("user2026");
    if (!token) return "";
    try {
        const payload = JSON.parse(
            decodeURIComponent(
                atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
                    .split('')
                    .map(function (c) {
                        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                    })
                    .join('')
            )
        );
        const scout = payload.scout || "";
        const team = payload.team || "";
        const security = payload.userContactLevel || "";
        const displayName = payload.displayName || "";
        return `${scout},${team},${security},${displayName}`;
    } catch (e) {
        return "";
    }
}

export const forceLogoutToLogin = (): void => {
    try {
        clearAuthUser();
        localStorage.removeItem("FBX_SELECTED_LIST");
        localStorage.removeItem("user2026");
    } catch (error) {
        console.error("Error during forced logout:", error);
    }

    try {
        window.dispatchEvent(new Event("fbx-logout"));
    } catch (error) {
        console.error("Error dispatching logout event:", error);
    }
};
